import axios from "axios";
import { Price } from "../models/Price";
import { Product } from "../models/Product";
import { Stock } from "../models/Stock";
import { DataPaginate } from "../models/utils/DataPaginate";
import { IFilter } from "../models/utils/filter/FilterType.interface";
import { convertFiltersToQuery, getParamsUrl } from "./utils/serviceHelper";
import {ProductDocument} from "../models/ProductDocument";

export interface ProductFilters {
  provider?: string;
  source?: string;
  brand_id?: string;
  is_platform?: string;
}

function list(
  page?: number | null,
  search?: string | null,
  filters?: IFilter[] | null,
  category_id?: number | null,
  productFilters?: ProductFilters | null
) {
  let params;
  if (filters) {
    params = `${convertFiltersToQuery(filters)}`;
  }
  let paramsUrl = getParamsUrl([
    { key: "page", value: page },
    { key: "search", value: search },
    { key: "category_id", value: category_id },
    // RAG-38 : filtres fournisseur / PIM source / marque
    { key: "provider", value: productFilters?.provider },
    { key: "source", value: productFilters?.source },
    { key: "brand_id", value: productFilters?.brand_id },
    // RAG-50 : filtre « produits de la Plateforme Socoda »
    { key: "is_platform", value: productFilters?.is_platform },
  ]);
  params = `${params ?? ""}${paramsUrl}`;

  return axios
    .get(`${process.env.REACT_APP_API_URL}products${params}`)
    .then((response: any) => {
      const dataPaginate = new DataPaginate(response.data);
      dataPaginate.setData(dataPaginate.data.map((item) => new Product(item)));

      return dataPaginate;
    });
}

/*
* Currently no taking account of filters
*/
function listWithElastic(searchValue: string, page: number): Promise<DataPaginate> {
    return new Promise((resolve, reject) => {
        axios.get(`${process.env.REACT_APP_API_URL}products/elasticsearch?search=${searchValue}&tag=backend&page=${page}`)
            .then((response) => {
                const dataPaginate = new DataPaginate(response.data, true);

                dataPaginate.setData(dataPaginate.results.map(item => new Product(item)))

                resolve(dataPaginate);
            })
            .catch((error) => {
                reject({status: error.rsponse.status, message: "Veuillez contacter un administrateur"});
            })
    })
}

function search(page: number, search?: string, filters?: IFilter[]){
  let params = `?page=${page}&tag=Backend`;
    if(search){
        params = `${params}&search=${search}`;
    }
    if(filters){
      params = `${params}${convertFiltersToQuery(filters)}`
    }

  return axios.get(`${process.env.REACT_APP_API_URL}products/elasticsearch${params}`)
    .then((response: any) => {
      const dataPaginate = new DataPaginate(response.data);
        dataPaginate.setData(dataPaginate.results.map(item => new Product(item)));

        return dataPaginate;
    })
}

function find(id: number) {
  return axios
    .get(`${process.env.REACT_APP_API_URL}products/${id}`)
    .then((response) => new Product(response.data));
}

function findProductPrice(id: number) {
  return axios
    .get(`${process.env.REACT_APP_API_URL}products/${id}/prices`)
    .then((response) => new Price(response.data));
}

function findProductStock(id: number): Promise<Stock[]> {
  return axios
    .get(`${process.env.REACT_APP_API_URL}products/${id}/stocks`)
    .then((response) =>
      response.data.stock.map((stock: any) => new Stock(stock))
    );
}

function getTotalStock(stocks: Stock[]) {
  return stocks.reduce((acc, cur) => {
    if (cur.stock >= 0) {
      return acc + cur.stock;
    }
    return acc;
  }, 0);
}

function importFile(file: File, type: string){
  const formData = new FormData();
  formData.append('file', file);
  return axios
    .post(`${process.env.REACT_APP_API_URL}products/import/${type}`, formData )
    .then((response) =>response.data);
}

function changeVisibility(id: number, visibility: boolean): Promise<number> {
    return new Promise((resolve, reject) => {
        axios.patch(`${process.env.REACT_APP_API_URL}products/${id}/visibility?visible=${visibility}`)
            .then((response) => {
                resolve(response.data.id);
            })
            .catch((error) => {
                switch (error.response.status) {
                    case 404:
                        reject({status: error.response.status, message: 'Produit inconnu'});
                        break;
                    default:
                        reject({status: error.response.status, message: "Veuillez contacter un administrateur"});
                }
            })
    })
}

function setCategory(id: number, categoryId: string | null): Promise<number> {
    return new Promise((resolve, reject) => {
        axios.patch(`${process.env.REACT_APP_API_URL}products/${id}/category`, { category_id: categoryId })
            .then((response) => {
                resolve(response.data.id);
            })
            .catch((error) => {
                switch (error.response?.status) {
                    case 404:
                        reject({status: 404, message: 'Produit inconnu'});
                        break;
                    case 422:
                        reject({status: 422, message: 'Catégorie invalide'});
                        break;
                    default:
                        reject({status: error.response?.status, message: "Veuillez contacter un administrateur"});
                }
            })
    })
}

function findProductDocuments(id: number): Promise<ProductDocument[]> {
    return new Promise((resolve, reject) => {
       axios.get(`${process.env.REACT_APP_API_URL}products/${id}/documents`)
           .then((response) => {
               if (response.data.length === 0) {
                   resolve([]);
                   return;
               }

               const productDocuments = response.data.map((productDocument: any) => new ProductDocument(productDocument));

               resolve(productDocuments);
           })
           .catch((error) => {
               switch (error.response.status) {
                   case 404:
                       reject({status: error.response.status, message: 'Produit inconnu'});
                       break;
                   default:
                       reject({status: error.response.status, message: "Erreur inconnue"});
               }
           })
    });
}

/**
 * RAG-45 — Sélection en masse : renvoie tous les produits d'un filtre (marque, gamme,
 * recherche) pour les ajouter d'un coup à une offre, sans les cocher un par un.
 * RAG-49 — étendu à la catégorie entière et au fournisseur entier (provider).
 */
function selectForBulk(filters: { brand_id?: string; category_id?: string; provider?: string; search?: string }): Promise<Product[]> {
    const params = new URLSearchParams();
    if (filters.brand_id) params.set("brand_id", filters.brand_id);
    if (filters.category_id) params.set("category_id", filters.category_id);
    if (filters.provider) params.set("provider", filters.provider);
    if (filters.search) params.set("search", filters.search);

    return axios
        .get(`${process.env.REACT_APP_API_URL}admins/products/select?${params.toString()}`)
        .then((response) => (response.data?.data ?? []).map((p: any) => new Product(p)));
}

/**
 * RAG-71 — Action EN MASSE : affecte une catégorie et/ou la visibilité à une sélection de
 * produits (surtout Plateforme Socoda) en une seule requête. Renvoie le nombre de produits
 * mis à jour.
 */
function bulkUpdate(ids: number[], payload: { category_id?: string | null; visible?: boolean }): Promise<number> {
    return new Promise((resolve, reject) => {
        axios.patch(`${process.env.REACT_APP_API_URL}admins/products/bulk`, { ids, ...payload })
            .then((response) => {
                resolve(response.data.updated);
            })
            .catch((error) => {
                switch (error.response?.status) {
                    case 422:
                        reject({ status: 422, message: "Sélection ou catégorie invalide" });
                        break;
                    default:
                        reject({ status: error.response?.status, message: "Veuillez contacter un administrateur" });
                }
            })
    })
}

export default {
    list,
    listWithElastic,
    search,
    find,
    findProductPrice,
    findProductStock,
    findProductDocuments,
    getTotalStock,
    importFile,
    changeVisibility,
    setCategory,
    selectForBulk,
    bulkUpdate,
}
